import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderSettingsSheetProps, WebsiteBuilderSettingsSheet } from "../../../../../helpers/WebsiteBuilderSettingsSheet";
import { SlideshowImagesAddView } from "./SlideshowImagesAddView";
import { SlideshowImagesAltTextView } from "./SlideshowImagesAltTextView";
import { SlideshowImagesManageView } from "./SlideshowImagesManageView";
import { SlideshowImagesReplaceView } from "./SlideshowImagesReplaceView";
import { IWebsiteBuilderSlideshowImage, IWebsiteBuilderSlideshowSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import { cloneDeep, isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { WebsiteBuilderSettingsRow } from "../../../../../helpers/WebsiteBuilderSettingsRow";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/pro-regular-svg-icons";

interface ISlideshowImagesSettingsProps {
  id: string;
  settings: IWebsiteBuilderSlideshowSettings;
}

interface ISlideshowImagesSettingsAddView {
  type: "add";
  selectedImages: IWebsiteBuilderSlideshowImage[];
}

interface ISlideshowImagesSettingsAltTextView {
  type: "alt-text";
  prevSelectedImages: IWebsiteBuilderSlideshowImage[];
  nextSelectedImages: IWebsiteBuilderSlideshowImage[];
}

interface ISlideshowImagesSettingsManageView {
  type: "manage";
}

interface ISlideshowImagesSettingsReplaceView {
  type: "replace";
  index: number;
  mediaId: number;
  selectedImage: IWebsiteBuilderSlideshowImage;
}

interface ISlideshowImagesSettingsNoneView {
  type: "none";
}

type TSlideshowImagesSettingsView =
  | ISlideshowImagesSettingsAddView
  | ISlideshowImagesSettingsAltTextView
  | ISlideshowImagesSettingsManageView
  | ISlideshowImagesSettingsReplaceView
  | ISlideshowImagesSettingsNoneView;

export function SlideshowImagesSettings(props: ISlideshowImagesSettingsProps) {
  const { id, settings } = props;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedImages, setUpdatedImages] = useState<IWebsiteBuilderSlideshowImage[]>(() => cloneDeep(settings.images));
  const [view, setView] = useState<TSlideshowImagesSettingsView>({ type: "none" });

  function setAddView(selectedImages: IWebsiteBuilderSlideshowImage[]) {
    setView({
      type: "add",
      selectedImages,
    });
  }

  function setAltTextView(prevSelectedImages: IWebsiteBuilderSlideshowImage[], nextSelectedImages: IWebsiteBuilderSlideshowImage[]) {
    setView({
      type: "alt-text",
      prevSelectedImages,
      nextSelectedImages,
    });
  }

  function setManageView() {
    setView({ type: "manage" });
  }

  function setReplaceView(
    index: number,
    mediaId: number,
    selectedImage: IWebsiteBuilderSlideshowImage,
  ) {
    setView({
      type: "replace",
      index,
      mediaId,
      selectedImage,
    });
  }

  function setNoneView() {
    setView({ type: "none" });
  }

  function cancelChanges() {
    setNoneView();
    setUpdatedImages(cloneDeep(settings.images));
  }

  function confirmChanges() {
    const prevSettings = cloneDeep(settings);
    const nextSettings = cloneDeep(settings);

    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "slideshow",
          settings: prevSettings,
        },
        nextContainerSettings: {
          type: "slideshow",
          settings: {
            ...nextSettings,
            images: cloneDeep(updatedImages),
          },
        },
      }),
    );

    setNoneView();
  }

  function getSettingsSheetProps(): IWebsiteBuilderSettingsSheetProps {
    const sharedProps: Pick<IWebsiteBuilderSettingsSheetProps, "size"> = {
      size: "medium",
    };

    switch (view.type) {
      case "add": {
        const imageCount = view.selectedImages.length;

        return {
          title: t("Add images"),
          supportText: t("Select the desired images to add it in the slideshow."),
          okText: t(imageCount === 1 ? "Add 1 image" : "Add {{imageCount}} images", { imageCount }),
          cancelText: t("Go back"),
          secondaryText: t("Add alt texts"),
          okDisabled: imageCount === 0,
          secondaryDisabled: imageCount === 0,
          onOk: () => {
            setUpdatedImages(prev => [...prev, ...view.selectedImages]);
            setManageView();
          },
          onCancel: setManageView,
          onSecondary: () => setAltTextView(cloneDeep(view.selectedImages), cloneDeep(view.selectedImages)),
          children:
            <SlideshowImagesAddView
              images={updatedImages}
              selectedImages={view.selectedImages}
              setSelectedImages={(selectedImages: IWebsiteBuilderSlideshowImage[]) => {
                setAddView(selectedImages);
              }}
            />,
          ...sharedProps,
        };
      }
      case "alt-text":
        return {
          title: t("Add alt text"),
          supportText: t("Add descriptive alt text to make your images accessible to all users."),
          okText: t("Confirm changes"),
          cancelText: t("Go back"),
          okDisabled: isEqual(view.prevSelectedImages, view.nextSelectedImages),
          onOk: () => {
            setAddView(cloneDeep(view.nextSelectedImages));
          },
          onCancel: () => {
            setAddView(cloneDeep(view.prevSelectedImages));
          },
          children:
            <SlideshowImagesAltTextView
              nextSelectedImages={view.nextSelectedImages}
              setNextSelectedImages={(updatedNextSelectedImages: IWebsiteBuilderSlideshowImage[]) => {
                setAltTextView(view.prevSelectedImages, updatedNextSelectedImages);
              }}
            />,
          ...sharedProps,
        };
      case "manage":
        return {
          title: t("Manage Images"),
          supportText: t("Add, replace, remove, or rearrange images to customize your slideshow."),
          okText: t("Confirm changes"),
          okDisabled: isEqual(settings.images, updatedImages),
          onOk: confirmChanges,
          onCancel: cancelChanges,
          children:
            <SlideshowImagesManageView
              images={updatedImages}
              setImages={setUpdatedImages}
              navigateToAdd={() => {
                setAddView([]);
              }}
              navigateToReplace={(index: number, image: IWebsiteBuilderSlideshowImage) => {
                setReplaceView(index, image.mediaId, cloneDeep(image));
              }}
            />,
          ...sharedProps,
        };
      case "replace":
        return {
          title: t("Replace image {{imageNumber}}", { imageNumber: view.index + 1 }),
          supportText: t("Select an image to replace the current one."),
          okText: t("Replace image"),
          cancelText: t("Go back"),
          okDisabled: view.selectedImage == null,
          onOk: () => {
            setUpdatedImages(prev => prev.map((image, imageIndex) =>
              imageIndex === view.index
                ? view.selectedImage
                : image,
            ));
            setManageView();
          },
          onCancel: setManageView,
          children:
            <SlideshowImagesReplaceView
              images={updatedImages}
              mediaId={view.mediaId}
              selectedImage={view.selectedImage}
              setSelectedImage={(selectedImage: IWebsiteBuilderSlideshowImage) => {
                setReplaceView(view.index, view.mediaId, selectedImage);
              }}
            />,
          ...sharedProps,
        };
      case "none":
      default:
        return null;
    }
  }

  const settingsSheetProps = getSettingsSheetProps();

  return (
    <>
      <WebsiteBuilderSettingsRow layout="space-between">
        <div className="website-builder-slideshow-images-settings-images-count">
          {t("Images ({{imagesCount}})", { imagesCount: settings.images.length })}
        </div>
        <Button className="website-builder-slideshow-images-settings-manage" size="small" onClick={setManageView}>
          <FontAwesomeIcon icon={faPen} />&nbsp;
          {t("Manage Images")}
        </Button>
      </WebsiteBuilderSettingsRow>
      {settingsSheetProps != null && <WebsiteBuilderSettingsSheet {...settingsSheetProps} />}
    </>
  );
}
